import { useState, useEffect, useCallback } from 'react';
import { it } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import { theme } from '../../theme';

function AssetCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await it.getAssetCategories();
      setCategories(r.data || []);
    } catch { setCategories([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (modal?.id) {
        await it.updateAssetCategory(modal.id, form);
      } else {
        await it.createAssetCategory(form);
      }
      setModal(null); setForm({ name: '', description: '' }); load();
    } catch { alert('Failed to save category.'); } finally { setSaving(false); }
  };

  const columns = [
    { header: 'Category Name', key: 'name' },
    { header: 'Description', key: 'description', render: (v) => v || '—' },
    { header: 'Asset Count', key: 'asset_count', render: (v) => v ?? '—' },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outline" onClick={() => { setModal(row); setForm({ name: row.name, description: row.description || '' }); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete category?')) { await it.deleteAssetCategory(row.id); load(); } }}>Delete</Button>
        </div>
      ),
    },
  ];

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Asset Categories</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Manage IT asset categories</p>
        </div>
        <Button variant="primary" onClick={() => { setModal({}); setForm({ name: '', description: '' }); }}>+ Add Category</Button>
      </div>

      <Card padding="0">
        <Table columns={columns} data={categories} loading={loading} emptyMessage="No categories found" />
      </Card>

      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={modal?.id ? 'Edit Category' : 'Add Category'}
        footer={<><Button variant="outlineGray" onClick={() => setModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleSave}>Save</Button></>}>
        <label style={lbl}>Category Name *</label>
        <input style={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Laptops, Monitors, Phones..." />
        <label style={lbl}>Description</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Modal>
    </div>
  );
}

export default AssetCategories;
